import {
  ReactElement,
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Stack,
  FormHelperText,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import { useFormikContext, FieldArray, ArrayHelpers } from 'formik';

import { useToggle } from '~hooks/useToggle';
import { getHelperText } from '~utils/formHelpers';

import DialogConfimation from '../DialogConfimation';

import { ListFieldChipProps } from './types';
import { ListFieldChipEmptyContainer, ListFieldChipItemChip } from './styles';

const ListFieldChip = ({
  name,
  emptyItemsTexts,
  dialogTexts,
  addItemPosition = 'first',
  disabled = false,
}: ListFieldChipProps): ReactElement => {
  const { values, errors, touched, setFieldTouched, getFieldMeta } =
    useFormikContext<{
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: string;
    }>();

  const toPathField = useCallback(
    (index: number) => `${name}[${index}]`,
    [name],
  );

  const listItems = useMemo(
    () => values[name] as unknown as string[],
    [values, name],
  );

  const error = useMemo(
    () => getHelperText(touched, errors, name),
    [errors, name, touched],
  );

  const [newItem, setNewItem] = useState('');

  const [openAddItemDialog, toggleAddItemDialog] = useToggle(false);

  const handleManualCloseAddItemDialog = useCallback(() => {
    setNewItem('');
    toggleAddItemDialog(false);
  }, [toggleAddItemDialog]);

  const handleChangeNewItem = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? event.currentTarget;

      setNewItem(value);
    },
    [],
  );

  const handleAddItemInList = useCallback(
    (insertHelper: ArrayHelpers['insert']) => {
      if (newItem) {
        const insertPositionIndex =
          addItemPosition === 'first' ? 0 : listItems.length;

        insertHelper(insertPositionIndex, newItem);

        setFieldTouched(name, true);

        handleManualCloseAddItemDialog();
      }
    },
    [
      newItem,
      addItemPosition,
      listItems,
      setFieldTouched,
      name,
      handleManualCloseAddItemDialog,
    ],
  );

  const handleRemoveItemInList = useCallback(
    (removeHelper: ArrayHelpers['remove'], index: number) => {
      removeHelper(index);

      setFieldTouched(name, true);
    },
    [setFieldTouched, name],
  );

  return (
    <FieldArray name={name}>
      {({ insert, remove }) => (
        <Stack>
          {listItems.length === 0 && (
            <ListFieldChipEmptyContainer>
              <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight="medium">
                  Todavía no cargaste ninguno
                </Typography>

                {emptyItemsTexts.example && (
                  <Typography variant="caption">
                    {`Por ejemplo: ${emptyItemsTexts.example}`}
                  </Typography>
                )}
              </Stack>

              <Button
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => toggleAddItemDialog(true)}
                sx={{ backgroundColor: 'common.white' }}
              >
                {emptyItemsTexts.buttonLabel}
              </Button>
            </ListFieldChipEmptyContainer>
          )}

          {listItems.length > 0 && (
            <Stack gap={1} direction="row" flexWrap="wrap">
              {listItems.map((_item, index) => (
                <ListFieldChipItemChip
                  key={toPathField(index)}
                  variant="outlined"
                  label={getFieldMeta<string>(toPathField(index)).value}
                  onDelete={() => handleRemoveItemInList(remove, index)}
                  disabled={disabled}
                />
              ))}

              <ListFieldChipItemChip
                variant="outlined"
                color="primary"
                label="+ Agregar"
                disabled={disabled}
                onClick={() => toggleAddItemDialog(true)}
              />
            </Stack>
          )}

          {error && (
            <FormHelperText variant="outlined" error>
              {error}
            </FormHelperText>
          )}

          <DialogConfimation
            open={openAddItemDialog}
            title={dialogTexts.title}
            description={dialogTexts.description}
            cancelText="Cancelar"
            onCancel={handleManualCloseAddItemDialog}
            confirmText="Agregar"
            onConfirm={() => handleAddItemInList(insert)}
          >
            <TextField
              label={dialogTexts.inputLabel}
              InputLabelProps={{ shrink: true }}
              size="small"
              value={newItem}
              onChange={handleChangeNewItem}
              fullWidth
              disabled={disabled}
            />
          </DialogConfimation>
        </Stack>
      )}
    </FieldArray>
  );
};

export default ListFieldChip;
